<!-- Renders a chat message's file attachments as an image grid plus a document list. -->

<script lang="ts" setup>
import { computed, ref, useCssModule, watch } from 'vue';

import type { ChatMessageAttachmentFile } from './ChatMessageAttachmentsTypes.js';
import ChatMessageDocumentRow from './ChatMessageDocumentRow.vue';
import ChatMessageImageTile from './ChatMessageImageTile.vue';

interface Props {
  files: ChatMessageAttachmentFile[];
  color?: 'blue' | 'white';
}

const MAX_VISIBLE_IMAGES = 4;

const props = defineProps<Props>();
const css = useCssModule('css');

const images = computed(() => props.files.filter(f => f.fileType?.startsWith('image/')));

const documents = computed(() => props.files.filter(f => !f.fileType?.startsWith('image/')));

/**
 * When true, all images are rendered regardless of count — toggled by clicking the
 * "+X more" overflow tile. Reset whenever the underlying files change.
 */
const expanded = ref(false);

watch(
  () => props.files,
  () => {
    expanded.value = false;
  },
);

const visibleImages = computed(() => (expanded.value ? images.value : images.value.slice(0, MAX_VISIBLE_IMAGES)));

const overflowCount = computed(() => {
  if (expanded.value) {
    return 0;
  }
  const total = images.value.length;
  return total > MAX_VISIBLE_IMAGES ? total - MAX_VISIBLE_IMAGES : 0;
});

function expandImages() {
  expanded.value = true;
}

const gridLayoutClass = computed(() => {
  const count = visibleImages.value.length;
  switch (count) {
    case 1:
      return css.grid_1;
    case 2:
      return css.grid_2;
    case 3:
      return css.grid_3;
    default:
      return css.grid_4plus;
  }
});
</script>

<template>
  <div :class="css.attachments">
    <div
      v-if="visibleImages.length"
      :class="[css.grid, gridLayoutClass]"
    >
      <ChatMessageImageTile
        v-for="(image, index) in visibleImages"
        :key="image.id"
        :file-name="image.fileName"
        :url="image.url"
        :preview-url="image.previewUrl"
        :width="image.width"
        :height="image.height"
        :preserve-aspect="visibleImages.length === 1"
        :overflow-count="index === MAX_VISIBLE_IMAGES - 1 ? overflowCount : null"
        :color="color"
        @overflow-click="expandImages"
      />
    </div>

    <div
      v-if="documents.length"
      :class="css.documents"
    >
      <ChatMessageDocumentRow
        v-for="doc in documents"
        :key="doc.id"
        :file-name="doc.fileName"
        :file-type="doc.fileType"
        :url="doc.url"
        :size="doc.size"
        :color="color"
      />
    </div>
  </div>
</template>

<style lang="scss" module="css">
$attachment-gap: $spacing-04;

.attachments {
  display: flex;
  flex-direction: column;
  gap: $attachment-gap;
  width: 100%;
  min-width: 0;
}

// The image grid carries a definite `width` so it anchors the bubble width to
// the image target (260/320) instead of letting a short doc filename squeeze
// the bubble — and `max-width: 100%` shrinks it inside narrow containers
// (resolved against the constrained `.inner` in ChatMessage). Using
// `min(target, 100%)` here would collapse to 0 during intrinsic sizing because
// `100%` resolves against an indeterminate basis.
.grid {
  display: grid;
  gap: $attachment-gap;
  max-width: 100%;
  min-width: 0;

  &_1 {
    grid-template-columns: 1fr;
    width: 260px;
  }

  &_2 {
    grid-template-columns: 1fr 1fr;
    width: 320px;
  }

  &_3 {
    grid-template-columns: 1fr 1fr 1fr;
    width: 320px;
  }

  &_4plus {
    grid-template-columns: 1fr 1fr;
    width: 320px;
  }
}

.documents {
  display: flex;
  flex-direction: column;
  gap: $attachment-gap;
  width: 100%;
  min-width: 0;
}
</style>
